// core
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';

// libraries
import { CookieService } from 'ngx-cookie-service';

// Campfire
import { PartnerBrandService } from '@campfire-nx/api';

// Raptor
import { RaptorStepDataService } from './raptor-step-data.service';
import { RaptorQuestionService } from './raptor-question.service';
import { RaptorGtmService } from './raptor-gtm.service';
import { RaptorDataService } from './raptor-data.service';
import { RaptorSurvicateService } from './raptor-survicate.service';
import { RAPTOR_TRACKING_OBJECT_NAMES } from '../constant/raptor-gtm-constants';

export interface IAnalyticsPayload {
  schema: string;
  source: string;
  channel: string;
  action: string;
  target: string;
  status: number;
  actors?: any[];
  request: {
    query?: {},
    cookies?: {},
    headers?: {}
  };
  data: {};
}
export interface IApiKey {
  token: string;
}

declare global {
  interface Window { analytics: any }
}

@Injectable()
export class RaptorAnalyticsService {
  config: any;
  analyticsSessionId: any;
  universalAnalyticsId: any;
  dataFlag: any;
  stepFiveSecs: any;
  stepTenSecs: any;
  stepThirtySecs: any;

  constructor(
    public http: HttpClient,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private cookieService: CookieService,
    private raptorStepDataService: RaptorStepDataService,
    private raptorQuestionService: RaptorQuestionService,
    private raptorGtmService: RaptorGtmService,
    private partnerBrandService: PartnerBrandService,
    private raptorDataService: RaptorDataService,
    private raptorSurvicateService: RaptorSurvicateService
  ) {
    let config: any = window.sessionStorage.getItem('config');
    if (typeof config === 'string') {
      this.config = JSON.parse(config);
    }

    let dataFlag = window.localStorage.getItem('da');
    this.dataFlag = dataFlag ? JSON.parse(dataFlag) : false;
    if (typeof this.dataFlag !== 'boolean') {
      window.localStorage.setItem('da', 'false');
    }

    this.analyticsSessionId = this.cookieService.get('_hat');
    this.universalAnalyticsId = this.cookieService.get('_huat');

    if (!this.analyticsSessionId) {
      this.analyticsSessionId = this.generateQuickGuid();
      this.cookieService.set('_hat', this.analyticsSessionId, { path: '/', secure: true });
    }

    if (!this.universalAnalyticsId) {
      this.universalAnalyticsId = this.generateQuickGuid();
      let expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + (365 * 10));
      this.cookieService.set('_huat', this.universalAnalyticsId, { path: '/', expires: expiryDate, secure: true });
    }
  }

  public startStep() {
    let formData = this.raptorStepDataService.get();

    if (formData.analytics) {
      this.trackEvent(
        'start-step',
        formData.analytics.flow_slug,
        formData.analytics.page_uuid,
        formData.analytics
      );

      this.stepFiveSecs = setTimeout(() => {
        this.trackEvent(
          'time-on-page',
          formData.analytics.flow_slug,
          formData.analytics.page_uuid,
          formData.analytics,
          { value: '5 seconds' }
        );
      }, 5000);
      this.stepTenSecs = setTimeout(() => {
        this.trackEvent(
          'time-on-page',
          formData.analytics.flow_slug,
          formData.analytics.page_uuid,
          formData.analytics,
          { value: '10 seconds' }
        );
      }, 10000);
      this.stepThirtySecs = setTimeout(() => {
        this.trackEvent(
          'time-on-page',
          formData.analytics.flow_slug,
          formData.analytics.page_uuid,
          formData.analytics,
          { value: '30 seconds' }
        );
      }, 30000);

      this.trackPartnerEvent('startStep', formData);
    }
  }

  public finishStep(navigatedBack?: boolean, step?: any) {
    // no data is passed when going back
    if (!navigatedBack) {
      let formData = this.raptorStepDataService.get();
      if (formData.analytics) {
        this.trackEvent(
          'submit-step',
          formData.analytics.flow_slug,
          formData.analytics.page_uuid,
          formData.analytics
        );
      }
      // Send data to Raptor GTM Service - submit events only
      this.trackPartnerEvent('submitStep', formData, step);
      this.raptorGtmService.trackQuestionOnSubmit(formData, step);
    }

    if (this.stepFiveSecs) {
      clearTimeout(this.stepFiveSecs);
    }
    if (this.stepTenSecs) {
      clearTimeout(this.stepTenSecs);
    }
    if (this.stepThirtySecs) {
      clearTimeout(this.stepThirtySecs);
    }
  }

  /**
   * 
   * @param step 
   * @param formData
   * @param paymentType
   * @description Handle partner payment events
   */
  public paymentEvent(step: any, formData: any, paymentType: any) {
    let referrer = this.partnerBrandService.getAttribute('referrer', 'partner');
    let isReferred = this.partnerBrandService.getAttribute('isReferred', 'partner');

    if (
      step &&
      step.data &&
      step.data.policy_number &&
      referrer &&
      referrer.slug === 'cashrewards' &&
      isReferred
    ) {
      // payment suceeded and the policy is bound
      if ((window as any).ire) {
        let product = this.partnerBrandService.getAttribute('product', 'partner');
        let selectedCover = this.raptorDataService.getSelectedCover(formData);
        let frequency = formData.data['frequency'];
        let premium = selectedCover.premium[frequency];
        let basePremium = premium.premium_details?.base_premium || 0;
        let arrangementFee = premium.extras?.arrang_fee?.premium_details?.base_premium || 0;
        let serviceFee = premium.extras?.SF?.premium_details?.base_premium || 0;
        let subTotal = parseFloat(basePremium) - parseFloat(arrangementFee) - parseFloat(serviceFee);
        
        (window as any).ire(
          'trackConversion',
          product?.cash_rewards?.trackConversion,
          {
            orderId: step.data.policy_number,
            text1: product.class,
            orderSubTotalPostDiscount: subTotal,
          },
          { verifySiteDefinitionMatch:true }
        );
      }
    } else if (step && step.data && step.data.policy_number) {
      // Send data to Raptor GTM Service - add payment info / purchase events only
      this.raptorGtmService.trackEventEcommerceAddPaymentInfo(formData, step, paymentType, this.partnerBrandService.getPartner());
      this.raptorGtmService.trackEventEcommercePurchase(formData, step, this.partnerBrandService.getPartner());
    }
  }

  public trackAdditionalEvents(eventName: string, eventValue: any, questionKey?: string, eventLabel?: string): void {
    let question;
    let formData = this.raptorStepDataService.get();
    if (questionKey) {
      question = this.raptorQuestionService.getQuestionByKey(formData, questionKey);
      if (question) {
        eventLabel = question.uuid;
      }
    }

    if (formData.analytics) {
      this.trackEvent(
        eventName,
        formData.analytics.flow_slug,
        eventLabel || formData.analytics.page_uuid,
        formData.analytics,
        { value: eventValue }
      );
    }
  }

  private getUser() {
    let user: any = window.localStorage.getItem('user');
    if (typeof user === 'string') {
      user = JSON.parse(user);
    }
    return user;
  }

  async trackEvent(eventName: string, eventCategory: string, eventLabel: string, analyticsObject: any, eventValue?: any) {
    const eventFullName = eventName + ' - ' + eventCategory + ' - ' + eventLabel;
    let user = this.getUser();
    let data: any = {
      eventValue: {
        info: eventValue,
        analytics: analyticsObject
      }
    };

    if (!eventName) throw Error('Metrics.trackEvent - missing required field eventName!');
    if (!eventCategory) throw Error('Metrics.trackEvent - missing required field eventCategory!');
    if (!eventLabel) throw Error('Metrics.trackEvent - missing required field eventLabel!');

    data.event = 'Beacon';
    data.eventName = eventName;
    data.eventCategory = eventCategory;
    data.eventLabel = eventLabel;
    data.eventUrl = this.router.url;
    data.userSlug = user ? user.slug : (analyticsObject.userSlug || '');
    data._hat = this.analyticsSessionId;
    data._huat = this.universalAnalyticsId;

    // AHM GTM events
    this.transformPartnerGtm({
      [RAPTOR_TRACKING_OBJECT_NAMES.event]: 'trackEvent',
      [RAPTOR_TRACKING_OBJECT_NAMES.eventAction]: eventName,
      [RAPTOR_TRACKING_OBJECT_NAMES.eventCategory]: eventCategory,
      [RAPTOR_TRACKING_OBJECT_NAMES.eventLabel]: eventLabel,
      [RAPTOR_TRACKING_OBJECT_NAMES.eventValue]: eventValue
    });

    this.raptorGtmService.trackEvent(
      this.raptorGtmService.buildStaticEventObject(
        'trackEvent',
        eventName,
        eventCategory,
        eventLabel,
        eventValue
      )
    );

    // Segment
    if (typeof window.analytics === 'object' && this.config.segmentID) {
      window.analytics.track(eventFullName, data);
    }

    // Maverick
    this.trackMaverick(data);

    // Debug Logging
    if (this.dataFlag) {
      // console.log('Raptor.trackEvent():', eventFullName, data);
    }
  }

  /**
   * @param formData - form data
   * @param type - set true to fire onload. submitStep| startStep
   * @param options - returned step data on submit
   * @description Construct page event from form data
   */
  public trackPartnerEvent(type: string, formData: any, options?: any) {
    if (formData.settings && formData.analytics) {
      if (Array.isArray(formData.settings.tracking)) {
        formData.settings.tracking.forEach((event: any) => {
          const flowSlug = formData.analytics.flow_slug;
          const pageUuid = formData.analytics.page_uuid;

          switch(event.type) {
            case 'page':
              if (type === 'submitStep') {
                this.raptorGtmService.trackEvent(this.raptorGtmService.buildStaticEventObject(
                  'trackEvent', 
                  'submit-step', 
                  flowSlug, 
                  pageUuid, 
                  formData.analytics
                ));
              }
            break;
            case 'dynamic':
              if (type === 'submitStep') {
                this.raptorGtmService.trackEvent(this.raptorGtmService.buildCustomEventObject(
                  event, 
                  options,
                  this.raptorGtmService.getEventValues(options, event)
                ));
              }
            break;

            case 'custom':
              if (type === 'submitStep') {
                this.raptorGtmService.trackEvent(this.raptorGtmService.buildStaticEventObject(
                  event.event,
                  event.action,
                  event.category ?? flowSlug,
                  event.label ?? pageUuid,
                  this.raptorGtmService.getEventValues(options, event)
                ));
              }
            break;

            case 'add_to_cart':
              if (type === 'submitStep') {
                this.raptorGtmService.trackEventEcommerceAddToCart(options, this.partnerBrandService.getPartner(), event);
              }
            break;

            case 'view_item':
              if (type === 'startStep') {
                this.raptorGtmService.trackEventEcommerceViewItem(formData, this.partnerBrandService.getPartner(), event);
              }
            break;

            case 'select_item':
              if (type === 'startStep') {
                this.raptorGtmService.trackEventEcommerceSelectItem(formData, this.partnerBrandService.getPartner(), event);
              }
            break;

            case 'begin_checkout':
              if (type === 'startStep') {
                this.raptorGtmService.trackEventEcommerceCheckout(formData, this.partnerBrandService.getPartner(), event);
              }
            break;

            case 'knockout':
              if (type === 'startStep') {
                this.raptorGtmService.trackEvent(this.raptorGtmService.buildStaticEventObject(
                  event.event,
                  event.action,
                  event.category ?? flowSlug,
                  event.label ?? pageUuid,
                  formData.analytics
                ));
              }
            break;

            case 'survicate':
              let survicateOptions = {
                brand: this.partnerBrandService.getPartner().slug,
                ...event.options
              }
              
              if (this.partnerBrandService.getAttribute('referrer', 'partner')) {
                survicateOptions.referrer = this.partnerBrandService.getAttribute('referrer', 'partner')
              }
              
              this.raptorSurvicateService.invokeEvent(event.action, survicateOptions);
            break;
          }
        });
      }
    } else {
      return;
    }
  }

  async trackMaverick(data: any) {
    const user = this.getUser();
    const cookieData = { _hat: data._hat, _huat: data._huat };
    let actors = [];
    let url: any;
    let apiKey;

    // console.log('trackMaverick() - ', data);

    // pick out the adword cookies
    const allCookies = this.cookieService.getAll();
    const adwordsCookies = (
      ({ _utm_campaign, _utm_content, _utm_medium, _utm_source, _utm_term}) => (
        { _utm_campaign, _utm_content, _utm_medium, _utm_source, _utm_term}
      )
    )(allCookies);
    // merge cookie data
    Object.assign(cookieData, adwordsCookies);

    if (user && user.slug && user.email) {
      actors.push({
        id: user.slug,
        email: user.email,
        phone: user.mobile,
        role: 'end-user'
      });
    }

    if (this.config) {
      url = this.config.maverickUrl || null;
      apiKey = this.config.maverickApiKey || null;
    }

    const payload: IAnalyticsPayload = {
      schema: 'v5',
      source: window.location.host,
      channel: 'portal',
      action: 'POST',
      target: data.eventName + '|' + data.eventCategory + '|' + data.eventLabel,
      status: 200,
      actors: actors,
      request: {
        cookies: cookieData,
        headers: {
          HTTP_HOST: window.location.host,
          HTTP_ORIGIN: window.location.origin,
          HTTP_REFERER: window.location.href,
          HTTP_USER_AGENT: window.navigator.userAgent
        }
      },
      data: data
    };

    const options = {
      headers: {
        'X-Api-Key': apiKey
      }
    };

    if (url && apiKey && data) {
      return this.http.post(`${url}`, payload, options)
        .toPromise()
        .catch((response: HttpErrorResponse) => {
          console.error('ERROR: Unable to log maverick interaction!', response);
        });
    } else {
      console.log('maverics url, api key or data was not provided');
      return;
    }
  }

  generateQuickGuid() {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }

  transformPartnerGtm(event: any) {
    let transformedEvent;
    let formData = this.raptorStepDataService.get();

    // transform AHM Car quote events
    if (formData.flow_slug === 'ahmCarNB2409') {
      if (event.eventAction === 'start-step') {
        if (formData.progress === 0) {
          transformedEvent = {
            category: 'car-quote',
            event: 'start-step',
            label: 'f1.preferred_cover'
          };
        }
        if (formData.progress === 1 && formData.total_pages === 1) {
          transformedEvent = {
            category: 'car-quote',
            event: 'start-step',
            label: 'multi-policy-purchasepage'
          };
        }
        if (formData.progress === 11) {
          transformedEvent = {
            category: 'car-quote',
            event: 'apply-for-price',
            label: 'f1.success'
          };
        }
      } 
    }
    if (transformedEvent && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(transformedEvent);
    }
  }

  public questionChangeEvent(question: any, questionValue: any) {
    if (question) {
      const tracking = question?.settings?.tracking;

      if (Array.isArray(tracking)) {
        tracking.forEach((event: any) => {
          if (event.type === 'dynamic') {
            this.raptorGtmService.trackEvent(this.raptorGtmService.buildCustomEventObject(
              event, 
              false,
              {
                [RAPTOR_TRACKING_OBJECT_NAMES.eventValue]: questionValue
              }
            ));
          }
          if (event.type === 'custom') {
            this.raptorGtmService.trackEvent(this.raptorGtmService.buildStaticEventObject(
              event.event,
              event.action,
              event.category ?? question.key,
              event.label ?? question.uuid,
              questionValue
            ));
          }
        });
      }
    } else {
      return;
    }
  }
}
